import {
  Avatar,
  Badge,
  Box,
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  SearchInput,
  Skeleton,
  Text,
} from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { getHighestUserRole, USER_ROLE_ATTRIBUTES } from 'lib-shared-user-role';
import { CSSProperties, useCallback } from 'react';
import { useEffectOnce } from 'react-use';

import { usersApi } from 'apis/users';
import { userModalActions } from 'components/usersModal';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useCurrentUser } from 'utils/user';

export const SettingsAgentsListView = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const {
    items: agents,
    total,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    changeArg,
    nextPage,
    previousPage,
    refetchItems,
    updateItem,
    search,
    arg,
  } = usePaginatedEndpoint({
    endpoint: usersApi.endpoints.readAgents,
    itemsSelector: ({ data: { agents } }) => agents,
    totalSelector: ({ data: { number_of_agents } }) => number_of_agents,
    initialArg: {
      limit: 50,
      offset: 0,
      sort: { key: 'display_name', direction: 'asc' },
    },
  });

  useEffectOnce(() => {
    const unsubscribe = pubSub.subscribe('userModalUserUpdated', (user) => {
      updateItem(user as User, (a, b) => a.id === b.id);
    });

    return () => unsubscribe();
  });

  const isSuccess = asyncStatus === 'fulfilled';
  const isError = asyncStatus === 'rejected';
  const isLoading = asyncStatus === 'pending';

  const handleSearchToggle = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && arg?.searchTerm) {
        changeArg({
          searchTerm: '',
        });
      }
    },
    [arg, changeArg],
  );

  const handleSearchSubmit = useCallback(
    (value: string) => {
      search(value, { debounceTime: 300 });
    },
    [search],
  );

  const handleAddAgent = useCallback(() => {
    ampli.settingsAgentsAddAgentButtonClicked();

    dispatch(
      userModalActions.show({
        user: { roles: ['agent'] },
      }),
    );
  }, [dispatch]);

  const {
    currentRole: { atLeastSystemadmin, atLeastAdmin },
    user,
  } = useCurrentUser();

  const { privilegeLevel: currentUserPrivilegeLevel } =
    USER_ROLE_ATTRIBUTES[getHighestUserRole(user.roles)];

  const columnStyle: CSSProperties = {
    width: '25%',
  };

  return (
    <Page.Section
      title="All agents"
      titleMeta={
        !!total && (
          <Badge size="small" variant="secondary">
            {total}
          </Badge>
        )
      }
      controls={
        <>
          <SearchInput
            onToggle={handleSearchToggle}
            onSubmit={handleSearchSubmit}
          />

          {atLeastSystemadmin && (
            <Button onClick={handleAddAgent} id="add-agent-btn">
              Add agent
            </Button>
          )}
        </>
      }
    >
      <DataTable
        columns={[
          {
            label: 'Name',
            selector: (agent) => {
              if (isLoading) {
                return (
                  <FlexBox alignItems="center" gap="300">
                    <Skeleton
                      width={sizes.spacer[500]}
                      height={sizes.spacer[500]}
                    />

                    <Skeleton height="14px" width="50%" />
                  </FlexBox>
                );
              }

              const { name, family_name, given_name } = agent;
              const fullName =
                `${given_name || ''} ${family_name || ''}`.trim() ||
                name?.trim();

              return (
                <FlexBox alignItems="center" gap="300">
                  <Box width={sizes.spacer[500]} minWidth={sizes.spacer[500]}>
                    <Avatar user={agent} />
                  </Box>

                  <Text.Paragraph>{fullName}</Text.Paragraph>
                </FlexBox>
              );
            },
            style: columnStyle,
          },
          {
            label: 'Display name',
            selector: (agent) => {
              if (isLoading) {
                return <Skeleton height="14px" width="50%" />;
              }
              return agent.name?.trim();
            },
            style: columnStyle,
          },
          {
            label: 'Email',
            selector: (agent) => {
              if (isLoading) {
                return <Skeleton height="14px" width="50%" />;
              }
              return agent.email;
            },
            style: columnStyle,
          },
          {
            label: 'Role',
            selector: (agent) => {
              if (isLoading) {
                return <Skeleton height="14px" width="50%" />;
              }
              return USER_ROLE_ATTRIBUTES[getHighestUserRole(agent.roles)]
                .prettyName;
            },
            style: columnStyle,
          },
          {
            label: '',
            selector: (agent) => {
              if (isLoading) {
                return (
                  <FlexBox
                    justifyContent="center"
                    alignItems="center"
                    width="32px"
                    height="32px"
                  >
                    <Skeleton height="18px" width="18px" />
                  </FlexBox>
                );
              }

              const { privilegeLevel } =
                USER_ROLE_ATTRIBUTES[getHighestUserRole(agent.roles)];

              if (atLeastAdmin || currentUserPrivilegeLevel < privilegeLevel) {
                return (
                  <IconButton
                    variant="tertiary"
                    icon="pen"
                    onClick={() => {
                      dispatch(userModalActions.show({ user: agent }));
                      ampli.settingsAgentsEditButtonClicked();
                    }}
                  />
                );
              }
            },
            style: {
              width: 0,
            },
          },
        ]}
        data={
          isLoading ? (Array.from({ length: 25 }) as typeof agents) : agents
        }
        paginationProps={{
          previousButtonDisabled: !hasPreviousPage || !isSuccess,
          previousButtonClick: previousPage,
          nextButtonDisabled: !hasNextPage || !isSuccess,
          nextButtonClick: nextPage,
        }}
        tableStatusProps={{
          loadingFailed: isError,
          onLoadingRetry: refetchItems,
          emptyState:
            !agents.length && isSuccess
              ? {
                  title: 'No agents found',
                  description: 'Try changing your filters or search criteria.',
                }
              : undefined,
        }}
        tableStyleProps={{
          stickyHeader: true,
          stickyFooter: true,
        }}
      />
    </Page.Section>
  );
};
